import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLocale } from '../contexts/LocaleContext';
import { useProjectAccess } from '../contexts/ProjectAccessContext';
import { API_BASE } from '../config';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Input } from './ui/input';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ProjectPanelLayout } from './ProjectPanelLayout';
import { CONCEPT_ICONS, PROJECT_SECTION_ICONS } from '../utils/concept-icons';
import { navigationClickHandlers } from '../utils/app-navigation';
import { textIncludesQuery } from '@narrativebench/editor-core';
import { ArrowUpDown, ArrowUp, ArrowDown, MoreHorizontal, Archive, ArchiveRestore } from 'lucide-react';
import { RotatingTrashIcon } from './RotatingTrashIcon';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { AddItemButton } from './AddItemButton';
import { EmptyStateAddButton } from './EmptyStateAddButton';
import { EmptyStateSecondaryButton } from './EmptyStateSecondaryButton';
import { getIconComponent } from './IconPicker';
import { useFileImport } from '../hooks/useFileImport';
import { buildDocumentContentBody } from '../utils/yjs-content-payload';
import { yjsUpdateFromDocumentApi } from '../utils/document-api-yjs';
import { useToast } from '../contexts/ToastContext';
import { TierLimitModal } from './TierLimitModal';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import * as Y from 'yjs';
import { createLiveYDoc, ydocToJSON } from '@narrativebench/editor-core';
import type { JSONContent } from '@tiptap/core';
import { useIsMobile } from '../hooks/use-mobile';
import { shouldShowHeaderCreateActions } from '../utils/header-create-actions';

const ShareIcon = CONCEPT_ICONS.share;

interface DocumentItem {
  id: string;
  title: string;
  icon: string | null;
  projectId: string | null;
  createdAt: string;
  updatedAt: string;
  wordCount?: number;
  pageEstimation?: number;
  archived?: boolean;
  ownerArchived?: boolean;
  sharedIntoProject?: boolean;
  ownerEmail?: string | null;
  sharePermission?: 'reader' | 'commenter' | 'editor' | null;
}

interface ProjectInfo {
  mainDocumentId: string | null;
}

const extractTextFromNode = (node: JSONContent): string => {
  if (node.type === 'text') {
    return node.text || '';
  }
  if (Array.isArray(node.content)) {
    return node.content.map(extractTextFromNode).join(' ');
  }
  return '';
};

const countWords = (text: string): number => {
  const trimmed = text.trim();
  if (!trimmed) {
    return 0;
  }
  const words = trimmed.split(/\s+/).filter((word) => word.length > 0);
  return words.length;
};

const estimatePages = (wordCount: number): number => {
  const wordsPerPage = 250;
  return wordCount === 0 ? 0 : Math.ceil(wordCount / wordsPerPage);
};

export const DocumentsTable: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { t } = useLocale();
  const { showToast, showErrorToast } = useToast();
  const { canEditProject } = useProjectAccess();
  const isMobile = useIsMobile();
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [projectInfo, setProjectInfo] = useState<ProjectInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [tierLimitModalOpen, setTierLimitModalOpen] = useState(false);
  const [tierLimitType, setTierLimitType] = useState<'project' | 'document' | 'resource'>('document');
  const searchDebounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);

  const fetchProjectInfo = React.useCallback(async (): Promise<void> => {
    if (!projectId || !token) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setProjectInfo({
          mainDocumentId: data.mainDocumentId || null,
        });
      }
    } catch (error) {
      console.error('Error fetching project info:', error);
    }
  }, [projectId, token]);

  const calculateWordCount = async (documentId: string): Promise<{ wordCount: number; pageEstimation: number }> => {
    try {
      const response = await fetch(`${API_BASE}/api/documents/${documentId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const contentUint8Array = yjsUpdateFromDocumentApi(data);
        if (contentUint8Array) {
          const ydoc = createLiveYDoc();
          try {
            Y.applyUpdate(ydoc, contentUint8Array);
            const json = ydocToJSON(ydoc);

            if (json) {
              const extractTextFromDocument = (node: JSONContent): string => {
                if (node.type === 'doc' && Array.isArray(node.content)) {
                  return node.content.map(extractTextFromNode).join(' ');
                }
                if (Array.isArray(node.content)) {
                  return node.content.map(extractTextFromNode).join(' ');
                }
                return extractTextFromNode(node);
              };

              const documentText = extractTextFromDocument(json);
              const wordCount = countWords(documentText);
              const pageEstimation = estimatePages(wordCount);
              return { wordCount, pageEstimation };
            }
          } finally {
            ydoc.destroy();
          }
        }
      }
    } catch (error) {
      console.error('Error calculating word count:', error);
    }
    return { wordCount: 0, pageEstimation: 0 };
  };

  const fetchDocuments = React.useCallback(async (searchQuery?: string, isInitial = false): Promise<void> => {
    if (!projectId || !token) {
      return;
    }

    try {
      if (isInitial) {
        setInitialLoading(true);
      } else {
        setLoading(true);
      }
      const query = showArchived ? '?includeArchived=true' : '';
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/documents${query}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        let projectDocuments = (data.documents || []) as DocumentItem[];

        if (searchQuery && searchQuery.trim()) {
          projectDocuments = projectDocuments.filter((doc: DocumentItem) =>
            textIncludesQuery(doc.title, searchQuery)
          );
        }

        const documentsWithWordCount = await Promise.all(
          projectDocuments.map(async (doc: DocumentItem) => {
            const { wordCount, pageEstimation } = await calculateWordCount(doc.id);
            return {
              ...doc,
              wordCount,
              pageEstimation,
            };
          })
        );

        setDocuments(documentsWithWordCount);
      }
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      if (isInitial) {
        setInitialLoading(false);
      } else {
        setLoading(false);
      }
    }
  }, [projectId, token, showArchived]);

  React.useEffect(() => {
    fetchProjectInfo();
    fetchDocuments(undefined, true);
  }, [projectId, token, fetchProjectInfo, fetchDocuments]);

  React.useEffect(() => {
    if (searchDebounceTimerRef.current) {
      clearTimeout(searchDebounceTimerRef.current);
    }

    if (!globalFilter) {
      fetchDocuments();
      return;
    }

    const timer = setTimeout(() => {
      fetchDocuments(globalFilter);
    }, 300);

    searchDebounceTimerRef.current = timer;

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [globalFilter, fetchDocuments]);

  React.useEffect(() => {
    const handleDocumentCreated = (): void => {
      fetchDocuments(globalFilter || undefined);
    };

    const handleDocumentDeleted = (): void => {
      fetchDocuments(globalFilter || undefined);
    };

    const handleDocumentArchived = (): void => {
      fetchDocuments(globalFilter || undefined);
    };

    const handleDocumentTitleUpdate = (): void => {
      fetchDocuments(globalFilter || undefined);
    };

    const handleProjectTitleUpdate = (event: Event): void => {
      const customEvent = event as CustomEvent<{ projectId: string; title: string }>;
      if (customEvent.detail.projectId === projectId) {
        fetchProjectInfo();
      }
    };

    window.addEventListener('document-created', handleDocumentCreated);
    window.addEventListener('document-deleted', handleDocumentDeleted);
    window.addEventListener('document-archived', handleDocumentArchived);
    window.addEventListener('document-title-updated', handleDocumentTitleUpdate);
    window.addEventListener('project-title-updated', handleProjectTitleUpdate);

    return () => {
      window.removeEventListener('document-created', handleDocumentCreated);
      window.removeEventListener('document-deleted', handleDocumentDeleted);
      window.removeEventListener('document-archived', handleDocumentArchived);
      window.removeEventListener('document-title-updated', handleDocumentTitleUpdate);
      window.removeEventListener('project-title-updated', handleProjectTitleUpdate);
    };
  }, [fetchDocuments, globalFilter, fetchProjectInfo, projectId]);

  const handleSetAsMainDocument = React.useCallback(async (documentId: string, e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    if (!token || !projectId) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/main-document`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ documentId }),
      });

      if (!response.ok) {
        throw new Error('Failed to set main document');
      }

      const document = documents.find((doc) => doc.id === documentId);
      if (document && projectInfo) {
        setProjectInfo({
          ...projectInfo,
          mainDocumentId: documentId,
        });
      }

      showToast(t('main_document_updated') || 'Main document updated');
      await fetchDocuments(globalFilter || undefined);
    } catch (err) {
      console.error('Error setting main document:', err);
      showErrorToast(t('failed_to_set_main_document') || 'Failed to set main document');
    }
  }, [projectId, token, t, fetchDocuments, globalFilter, showToast, showErrorToast, documents, projectInfo]);

  const setDocumentArchived = React.useCallback(async (
    documentId: string,
    archived: boolean,
    e: React.MouseEvent
  ): Promise<void> => {
    e.stopPropagation();
    if (!projectId || !token) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/api/projects/${projectId}/documents/${documentId}/archived`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ archived }),
        }
      );

      if (response.ok) {
        fetchDocuments(globalFilter || undefined);
        showToast(
          archived
            ? (t('document_archived') || 'Document archived')
            : (t('document_unarchived') || 'Document unarchived')
        );
        window.dispatchEvent(new CustomEvent(archived ? 'document-archived' : 'document-unarchived', {
          detail: { documentId, projectId },
        }));
      } else {
        const error = await response.json().catch(() => ({} as { error?: string }));
        showErrorToast(
          error.error
          || (archived ? t('failed_to_archive_document') : t('failed_to_unarchive_document'))
        );
      }
    } catch (error) {
      console.error(archived ? 'Error archiving document:' : 'Error unarchiving document:', error);
      showErrorToast(archived ? t('failed_to_archive_document') : t('failed_to_unarchive_document'));
    }
  }, [projectId, token, t, fetchDocuments, globalFilter, showToast, showErrorToast]);

  const handleArchiveDocument = React.useCallback(async (documentId: string, e: React.MouseEvent): Promise<void> => {
    await setDocumentArchived(documentId, true, e);
  }, [setDocumentArchived]);

  const handleUnarchiveDocument = React.useCallback(async (documentId: string, e: React.MouseEvent): Promise<void> => {
    await setDocumentArchived(documentId, false, e);
  }, [setDocumentArchived]);

  const handleDeleteDocument = React.useCallback(async (documentId: string, e: React.MouseEvent): Promise<void> => {
    e.stopPropagation();
    if (!projectId || !token) {
      return;
    }

    if (!confirm(t('Are you sure you want to delete this document?'))) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        fetchDocuments(globalFilter || undefined);
        showToast(t('document_moved_to_trash') || 'Document moved to trash');
      } else {
        const error = await response.json().catch(() => ({} as { error?: string }));
        showErrorToast(error.error || t('failed_to_delete_document'));
      }
    } catch (error) {
      console.error('Error deleting document:', error);
      showErrorToast(t('failed_to_delete_document'));
    }
  }, [projectId, token, t, fetchDocuments, globalFilter, showToast, showErrorToast]);

  const { handleImportFile, isImportingFile } = useFileImport({
    token: token || '',
    t,
    showToast,
    createDocument: async (title: string, content?: Uint8Array) => {
      if (!projectId) {
        throw new Error('Project ID is required');
      }

      const body: { title: string; contentBase64?: string } = { title };
      if (content) {
        Object.assign(body, buildDocumentContentBody(content));
      }

      const response = await fetch(`${API_BASE}/api/projects/${projectId}/documents`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 403 && errorData.errorCode === 'DOCUMENT_LIMIT_REACHED') {
          setTierLimitType('document');
          setTierLimitModalOpen(true);
          throw new Error('DOCUMENT_LIMIT_REACHED');
        }
        throw new Error('Failed to create document');
      }

      return await response.json();
    },
    onSuccess: async () => {
      await fetchDocuments(globalFilter || undefined);
      if (projectId) {
        window.dispatchEvent(
          new CustomEvent('document-created', {
            detail: { projectId },
          })
        );
      }
    },
  });

  const handleCreateDocument = async (): Promise<void> => {
    if (!token || !projectId) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/projects/${projectId}/documents`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          title: t('untitled_document'),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 403 && errorData.errorCode === 'DOCUMENT_LIMIT_REACHED') {
          setTierLimitType('document');
          setTierLimitModalOpen(true);
          return;
        }
        throw new Error('Failed to create document');
      }

      const data = await response.json();
      await fetchDocuments(globalFilter || undefined);
      window.dispatchEvent(
        new CustomEvent('document-created', {
          detail: {
            documentId: data.id,
            projectId,
            title: data.title,
          },
        })
      );
      navigate(`/projects/${projectId}/documents/${data.id}`);
    } catch (error) {
      console.error('Error creating document:', error);
      alert(t('failed_to_create_document'));
    }
  };

  const columns = useMemo<ColumnDef<DocumentItem>[]>(
    () => {
      const cols: ColumnDef<DocumentItem>[] = [
        {
          accessorKey: 'icon',
          header: () => <div></div>,
          cell: ({ row }) => {
            const icon = row.original.icon;
            if (icon) {
              const IconComponent = getIconComponent(icon);
              return IconComponent ? (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <IconComponent className="h-4 w-4" />
                </div>
              ) : (
                <div className="h-4 w-4 rounded border border-dashed bg-muted border-border" />
              );
            }
            return (
              <div className="h-4 w-4 rounded border border-dashed bg-muted border-border" />
            );
          },
          enableSorting: false,
        },
        {
          accessorKey: 'title',
          header: ({ column }) => {
            return (
              <button
                onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                className="flex items-center gap-2 hover:bg-muted px-2 py-1 rounded"
              >
                {t('Name')}
                {column.getIsSorted() === 'asc' ? (
                  <ArrowUp className="h-4 w-4" />
                ) : column.getIsSorted() === 'desc' ? (
                  <ArrowDown className="h-4 w-4" />
                ) : (
                  <ArrowUpDown className="h-4 w-4" />
                )}
              </button>
            );
          },
          cell: ({ row }) => {
            const permissionLabel = row.original.sharePermission === 'reader'
              ? t('permission_reader')
              : row.original.sharePermission === 'commenter'
                ? t('permission_commenter')
                : row.original.sharePermission === 'editor'
                  ? t('permission_editor')
                  : null;
            const authorLabel =
              row.original.ownerEmail &&
              user?.email &&
              row.original.ownerEmail !== user.email
                ? row.original.ownerEmail
                : null;
            const showSharedMeta = Boolean(authorLabel || permissionLabel);
            return (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <span>{row.original.title}</span>
                  {projectInfo?.mainDocumentId === row.original.id && (
                    <Badge variant="secondary" style={{ fontSize: '0.75rem' }}>
                      {t('main')}
                    </Badge>
                  )}
                  {row.original.archived && (
                    <Badge variant="outline" style={{ fontSize: '0.75rem' }}>
                      {t('archived')}
                    </Badge>
                  )}
                </div>
                {showSharedMeta ? (
                  <span className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
                    {authorLabel ? <span className="truncate">{authorLabel}</span> : null}
                    {authorLabel && permissionLabel ? <span aria-hidden>·</span> : null}
                    {permissionLabel ? (
                      <span className="inline-flex min-w-0 items-center gap-1">
                        {row.original.sharedIntoProject ? (
                          <ShareIcon className="h-3 w-3 shrink-0" aria-hidden />
                        ) : null}
                        <span className="truncate">{permissionLabel}</span>
                      </span>
                    ) : null}
                  </span>
                ) : null}
              </div>
            );
          },
        },
        {
          accessorKey: 'wordCount',
          header: ({ column }) => {
            return (
              <button
                onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                className="flex items-center gap-2 hover:bg-muted px-2 py-1 rounded"
              >
                {t('word_count')}
                {column.getIsSorted() === 'asc' ? (
                  <ArrowUp className="h-4 w-4" />
                ) : column.getIsSorted() === 'desc' ? (
                  <ArrowDown className="h-4 w-4" />
                ) : (
                  <ArrowUpDown className="h-4 w-4" />
                )}
              </button>
            );
          },
          cell: ({ row }) => {
            const wordCount = row.original.wordCount ?? 0;
            return <div>{wordCount.toLocaleString()}</div>;
          },
          sortingFn: (rowA, rowB) => {
            const countA = rowA.original.wordCount ?? 0;
            const countB = rowB.original.wordCount ?? 0;
            return countA - countB;
          },
        },
        {
          accessorKey: 'pageEstimation',
          header: ({ column }) => {
            return (
              <button
                onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                className="flex items-center gap-2 hover:bg-muted px-2 py-1 rounded"
              >
                <span className="capitalize">{t('pages')}</span>
                {column.getIsSorted() === 'asc' ? (
                  <ArrowUp className="h-4 w-4" />
                ) : column.getIsSorted() === 'desc' ? (
                  <ArrowDown className="h-4 w-4" />
                ) : (
                  <ArrowUpDown className="h-4 w-4" />
                )}
              </button>
            );
          },
          cell: ({ row }) => {
            const pageEstimation = row.original.pageEstimation ?? 0;
            return <div>{pageEstimation}</div>;
          },
          sortingFn: (rowA, rowB) => {
            const pagesA = rowA.original.pageEstimation ?? 0;
            const pagesB = rowB.original.pageEstimation ?? 0;
            return pagesA - pagesB;
          },
        },
        {
          accessorKey: 'createdAt',
          header: ({ column }) => {
            return (
              <button
                onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                className="flex items-center gap-2 hover:bg-muted px-2 py-1 rounded"
              >
                {t('created')}
                {column.getIsSorted() === 'asc' ? (
                  <ArrowUp className="h-4 w-4" />
                ) : column.getIsSorted() === 'desc' ? (
                  <ArrowDown className="h-4 w-4" />
                ) : (
                  <ArrowUpDown className="h-4 w-4" />
                )}
              </button>
            );
          },
          cell: ({ row }) => {
            const date = new Date(row.original.createdAt);
            return <div>{date.toLocaleDateString()}</div>;
          },
          sortingFn: (rowA, rowB) => {
            const dateA = new Date(rowA.original.createdAt).getTime();
            const dateB = new Date(rowB.original.createdAt).getTime();
            return dateA - dateB;
          },
        },
        {
          accessorKey: 'updatedAt',
          header: ({ column }) => {
            return (
              <button
                onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
                className="flex items-center gap-2 hover:bg-muted px-2 py-1 rounded"
              >
                {t('updated')}
                {column.getIsSorted() === 'asc' ? (
                  <ArrowUp className="h-4 w-4" />
                ) : column.getIsSorted() === 'desc' ? (
                  <ArrowDown className="h-4 w-4" />
                ) : (
                  <ArrowUpDown className="h-4 w-4" />
                )}
              </button>
            );
          },
          cell: ({ row }) => {
            const date = new Date(row.original.updatedAt);
            return <div>{date.toLocaleDateString()}</div>;
          },
          sortingFn: (rowA, rowB) => {
            const dateA = new Date(rowA.original.updatedAt).getTime();
            const dateB = new Date(rowB.original.updatedAt).getTime();
            return dateA - dateB;
          },
        },
      ];
      if (canEditProject) {
        cols.push({
          id: 'actions',
          header: () => <div></div>,
          cell: ({ row }) => {
            const canUnarchive =
              Boolean(row.original.archived)
              && (
                !row.original.ownerArchived
                || Boolean(user?.email && row.original.ownerEmail === user.email)
              );
            return (
              <div onClick={(e) => e.stopPropagation()}>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {!row.original.sharedIntoProject
                      && projectInfo?.mainDocumentId !== row.original.id
                      && !row.original.archived && (
                      <DropdownMenuItem
                        onClick={(e) => handleSetAsMainDocument(row.original.id, e)}
                      >
                        {t('set_as_main_document')}
                      </DropdownMenuItem>
                    )}
                    {row.original.archived ? (
                      canUnarchive ? (
                        <DropdownMenuItem
                          onClick={(e) => handleUnarchiveDocument(row.original.id, e)}
                        >
                          <ArchiveRestore className="mr-2 h-4 w-4" />
                          {t('unarchive_document')}
                        </DropdownMenuItem>
                      ) : null
                    ) : (
                      <DropdownMenuItem
                        onClick={(e) => handleArchiveDocument(row.original.id, e)}
                      >
                        <Archive className="mr-2 h-4 w-4" />
                        {t('archive_document')}
                      </DropdownMenuItem>
                    )}
                    {!row.original.sharedIntoProject && (
                      <DropdownMenuItem
                        onClick={(e) => handleDeleteDocument(row.original.id, e)}
                        className="group text-red-600"
                      >
                        <RotatingTrashIcon className="mr-2" />
                        {t('move_to_trash')}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            );
          },
          enableSorting: false,
        });
      }
      return cols;
    },
    [t, handleDeleteDocument, handleArchiveDocument, handleUnarchiveDocument, handleSetAsMainDocument, projectInfo, canEditProject, user?.email]
  );

  const table = useReactTable({
    data: documents,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    state: {
      sorting,
      globalFilter,
    },
  });

  const documentsActions = projectId && canEditProject && shouldShowHeaderCreateActions(documents.length > 0, isMobile) ? (
    <div className="flex items-center gap-2">
      <AddItemButton
        onClick={handleImportFile}
        disabled={isImportingFile}
        label={isImportingFile ? t('importing') : t('import_document')}
      />
      <AddItemButton
        onClick={handleCreateDocument}
        label={t('new document')}
      />
    </div>
  ) : undefined;

  if (initialLoading) {
    return (
      <ProjectPanelLayout title={t('Documents')} titleIcon={PROJECT_SECTION_ICONS.documents} actions={documentsActions} loading />
    );
  }

  return (
    <>
      <ProjectPanelLayout title={t('Documents')} titleIcon={PROJECT_SECTION_ICONS.documents} actions={documentsActions}>
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div className="mb-4 flex items-center gap-3">
            <Input
              type="text"
              placeholder={t('Search documents...')}
              value={globalFilter}
              onChange={(e) => {
                setGlobalFilter(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }}
              className="min-w-0 flex-1 bg-background"
            />
            <div className="flex shrink-0 items-center gap-2">
              <Checkbox
                id="show-archived-documents"
                checked={showArchived}
                onCheckedChange={(checked) => setShowArchived(checked === true)}
              />
              <Label htmlFor="show-archived-documents" className="whitespace-nowrap text-sm font-normal text-muted-foreground cursor-pointer">
                {t('show_archived')}
              </Label>
            </div>
          </div>
          <div
            className="border border-border rounded-lg overflow-auto flex-1 flex flex-col bg-card"
          >
            <Table style={{ width: '100%', height: '100%' }}>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} style={{ textAlign: 'center', padding: '2rem' }}>
                      <div className="flex flex-col items-center gap-4">
                        <span>{t('No documents found')}</span>
                        {documents.length === 0 && canEditProject ? (
                          <div className="flex flex-wrap items-center justify-center gap-2">
                            <EmptyStateSecondaryButton onClick={handleImportFile} disabled={isImportingFile}>
                              {isImportingFile ? t('importing') : t('import_document')}
                            </EmptyStateSecondaryButton>
                            <EmptyStateAddButton onClick={handleCreateDocument}>
                              {t('new document')}
                            </EmptyStateAddButton>
                          </div>
                        ) : null}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      {...navigationClickHandlers(
                        navigate,
                        `/projects/${projectId}/documents/${row.original.id}`
                      )}
                      style={{ cursor: 'pointer' }}
                      className="hover:bg-muted"
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </ProjectPanelLayout>
      <TierLimitModal open={tierLimitModalOpen} onOpenChange={setTierLimitModalOpen} limitType={tierLimitType} />
    </>
  );
};

